import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import SalesOrderDetailScreen from "./SalesOrderDetailScreen.jsx";
import {
  AC_SECTION_META,
  AC_SECTION_ORDER,
  customersFromOrders,
  filterOrders,
  listRiskLevel,
  loadActionItems,
  lateVerdict,
  loadSalesOrders,
  stagesFromOrders,
} from "../../features/salesOrders/soData";
import { fmtDay } from "./salesOrders/format";
import "../mobile.css";

// The phone Sales Orders page (plan Phase 2): the order list with search, status chips and filters,
// and the Action Center; ?id=N opens one order (SalesOrderDetailScreen). Creating an order, editing
// lines, packing plans, T&M pricing, invoices and emails stay on the desktop page.
// Layout only: features/salesOrders/soData.js is the desktop page's own code.

const RISK = {
  on_track: { label: "On track", cls: "jq-status jq-status-done" },
  behind: { label: "Behind", cls: "jq-status jq-status-cancelled" },
  overdue: { label: "Overdue", cls: "jq-status jq-status-cancelled" },
  complete: { label: "Complete", cls: "jq-status jq-status-done" },
  no_date: { label: "No date", cls: "jq-status jq-status-pending" },
};
const FULFILL = { pickup: "Pickup", delivery: "We deliver", shipping: "Carrier" };

function orderId(searchParams) {
  const raw = searchParams.get("id");
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

const LIST_START = { view: "orders", searchTerm: "", showClosed: false, showArchived: false, customerFilter: "", statusFilter: [] };

export default function SalesOrdersScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The list's view and filters live here, so coming back from an order finds them as they were.
  const [listState, setListState] = useState(LIST_START);
  const navigate = useNavigate();
  // Opened from the list: All orders = Back (one history step). Opened from a link: replace it.
  const fromList = useRef(false);
  const id = orderId(searchParams);
  const toList = () => {
    if (fromList.current) { fromList.current = false; navigate(-1); } else setSearchParams({}, { replace: true });
  };
  if (id) return <SalesOrderDetailScreen key={id} soId={id} onAll={toList} />;
  return <OrderList state={listState} setState={setListState} onOpen={(soId) => { fromList.current = true; setSearchParams({ id: String(soId) }); }} />;
}

function OrderCard({ o }) {
  const risk = RISK[listRiskLevel(o)];
  return (
    <div data-testid={`so-card-${o.sales_order_id}`}>
      <div className="jq-qcard-top">
        <span className="jq-qcard-job">{o.order_number}</span>
        {o.lifecycle_stage && <span className="jq-tag">{o.lifecycle_stage}</span>}
        {risk && <span className={risk.cls}>{risk.label}</span>}
      </div>
      <div className="jq-qcard-title">{o.customer_name}</div>
      <div className="jq-qcard-meta">
        {o.customer_po ? `PO ${o.customer_po}` : "No PO"}
        {o.promised_date ? ` · promised ${fmtDay(o.promised_date)}` : ""}
        {FULFILL[o.fulfillment_type] ? ` · ${FULFILL[o.fulfillment_type]}` : ""}
      </div>
      {Number(o.total_ordered) > 0 && (
        <div className="jq-muted">{Math.round(Number(o.percent_shipped) || 0)}% shipped · {Math.round(Number(o.production_progress_pct) || 0)}% made</div>
      )}
    </div>
  );
}

function OrderList({ state, setState, onOpen }) {
  const { view, searchTerm, showClosed, showArchived, customerFilter, statusFilter } = state;
  const set = (key) => (value) => setState((prev) => ({ ...prev, [key]: typeof value === "function" ? value(prev[key]) : value }));
  const setView = set("view");
  const setSearchTerm = set("searchTerm");
  const setShowClosed = set("showClosed");
  const setShowArchived = set("showArchived");
  const setCustomerFilter = set("customerFilter");
  const setStatusFilter = set("statusFilter");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    loadSalesOrders({ showClosed, showArchived })
      .then((rows) => {
        if (!live) return;
        if (rows) { setOrders(rows); setError(null); } else setError("Couldn't load the orders.");
      })
      .catch((err) => { if (live) setError(err.message || "Couldn't reach JobQ."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [showClosed, showArchived]);

  const customers = useMemo(() => customersFromOrders(orders), [orders]);
  const stages = useMemo(() => stagesFromOrders(orders), [orders]);
  const shown = useMemo(
    () => filterOrders(orders, { searchTerm, statusFilter, customerFilter }),
    [orders, searchTerm, statusFilter, customerFilter],
  );
  const toggleStage = (st) => setStatusFilter((prev) => (prev.includes(st) ? prev.filter((x) => x !== st) : [...prev, st]));
  const activeCount = statusFilter.length + (customerFilter ? 1 : 0) + (showClosed ? 1 : 0) + (showArchived ? 1 : 0);

  return (
    <div className="jq-screen" data-testid="phone-sales-orders">
      <div className="d-flex gap-2 mb-2" role="group" aria-label="View">
        <button type="button" className={`btn btn-sm ${view === "orders" ? "btn-secondary" : "btn-outline-secondary"}`} aria-pressed={view === "orders"} onClick={() => setView("orders")}>Orders</button>
        <button type="button" className={`btn btn-sm ${view === "action" ? "btn-secondary" : "btn-outline-secondary"}`} aria-pressed={view === "action"} onClick={() => setView("action")}>Action Center</button>
      </div>
      {view === "action" ? (
        <ActionCenter onOpen={onOpen} />
      ) : (
        <CardList
          items={shown}
          getKey={(o) => o.sales_order_id}
          onItemClick={(o) => onOpen(o.sales_order_id)}
          renderCard={(o) => <OrderCard o={o} />}
          loading={loading}
          error={error}
          empty={orders.length ? "No orders match." : "No open orders."}
          testId="so-list"
          search={{ value: searchTerm, onChange: setSearchTerm, placeholder: "Order #, PO or customer SO" }}
          filters={{
            activeCount,
            resultLabel: `${shown.length} order${shown.length === 1 ? "" : "s"}`,
            onClear: () => { setStatusFilter([]); setCustomerFilter(""); setShowClosed(false); setShowArchived(false); },
            render: () => (
              <>
                <label className="form-check form-switch mb-2">
                  <input className="form-check-input" type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
                  <span className="form-check-label">Show completed / shipped</span>
                </label>
                <label className="form-check form-switch mb-3">
                  <input className="form-check-input" type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                  <span className="form-check-label">Show archived</span>
                </label>
                <label className="form-label" htmlFor="so-customer">Customer</label>
                <select id="so-customer" className="form-select mb-3" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
                  <option value="">All customers ({customers.length})</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div className="form-label">Status</div>
                <div className="d-flex flex-wrap gap-2">
                  {stages.map((st) => (
                    <button key={st} type="button" className={`btn btn-sm ${statusFilter.includes(st) ? "btn-primary" : "btn-outline-primary"}`} aria-pressed={statusFilter.includes(st)} onClick={() => toggleStage(st)}>
                      {st}
                    </button>
                  ))}
                </div>
              </>
            ),
          }}
        />
      )}
    </div>
  );
}

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

/** One line per order under each Action Center section (the desktop draws each section its own way). */
function acDetail(key, item) {
  switch (key) {
    case "behind_schedule": // the desktop's risk chip wording
      return item.risk_level === "overdue" ? `Overdue ${item.days_late}d` : item.risk_level === "behind" ? `Est ${item.days_late}d late` : "At Risk";
    case "material_late": {
      const first = (item.items || [])[0];
      const late = lateVerdict(item).label(item);
      return first ? `${late} · ${first.material_code} lands ${fmtDay(first.ready_date)}` : late;
    }
    case "needs_more_jobs":
      return item.total_short_qty ? `${item.total_short_qty} pcs not yet in production` : "";
    case "ready_to_ship":
      return item.reason || "";
    case "ready_to_seal":
      return item.boxes_ready_to_seal ? `${item.boxes_ready_to_seal} box(es) ready to seal` : "";
    case "awaiting_pickup":
      return item.staged_pallets ? `${item.staged_pallets} pallet(s) staged` : "";
    case "uninvoiced":
      return item.uninvoiced_amount ? `${money(item.uninvoiced_amount)} not invoiced` : "";
    case "aging_drafts":
      return item.age_days != null ? `${item.age_days} days old` : "";
    default:
      return item.reason || "";
  }
}

function ActionCenter({ onOpen }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({});

  useEffect(() => {
    let live = true;
    loadActionItems()
      .then((d) => { if (live) setData(d); })
      .catch((err) => { if (live) setError(err.message || "Couldn't load the Action Center."); });
    return () => { live = false; };
  }, []);

  if (error) return <div className="jq-note jq-note-danger" role="alert">{error}</div>;
  if (!data) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;
  const cats = data.categories || {};
  const summary = data.summary || {};
  const sections = AC_SECTION_ORDER.filter((k) => (cats[k] || []).length > 0);
  return (
    <div data-testid="so-action-center">
      <div className="jq-card">
        <div className="fw-semibold">{summary.orders_needing_action || 0} order(s) need action</div>
        <div className="jq-muted">
          {summary.overdue_count ? `${summary.overdue_count} overdue · ` : ""}{money(summary.value_at_risk)} at risk
        </div>
      </div>
      {sections.length === 0 && <div className="jq-list-state">Nothing needs action.</div>}
      {sections.map((k) => {
        const meta = AC_SECTION_META[k];
        const items = cats[k];
        const shownItems = open[k] ? items : items.slice(0, 5);
        return (
          <section key={k} className="jq-card" data-testid={`ac-${k}`}>
            <h2 className="jq-section-title d-flex align-items-center mb-1">
              <span className="flex-grow-1">{meta.title}</span>
              <span className="jq-tag">{items.length}</span>
            </h2>
            <div className="jq-muted mb-2">{meta.blurb}</div>
            {shownItems.map((item) => (
              <button key={`${k}-${item.sales_order_id}`} type="button" className="jq-task-line btn btn-link text-reset text-decoration-none w-100 text-start p-0" onClick={() => onOpen(item.sales_order_id)}>
                <span className="jq-task-line-main">
                  <span className="fw-semibold">{item.order_number}</span> {item.customer_name}
                  {acDetail(k, item) && <div className="jq-muted">{acDetail(k, item)}</div>}
                </span>
                <i className="fas fa-chevron-right jq-muted" aria-hidden="true" />
              </button>
            ))}
            {!open[k] && items.length > 5 && (
              <button type="button" className="btn btn-outline-secondary jq-show-more mt-2" onClick={() => setOpen((o) => ({ ...o, [k]: true }))}>
                Show all ({items.length})
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}
